<template>
    <div class="meta-is">
        <div class="meta-is__info">
            <span class="meta-is__info-title">Business Name</span>
            <a v-if="metaOverwrites?.fbDisplayLink" :href="safeUrl">
                {{ metaOverwrites.fbDisplayLink }}
            </a>
            <button v-if="metaOverwrites?.fbCallToAction">{{ metaOverwrites.fbCallToAction }}</button>
        </div>

        <div class="meta-is__header">
            <div class="meta-is__progress-bar">
                <div class="meta-is__progress" />
            </div>

            <div class="meta-is__avatar">
                <Icon type="logo-facebook" />
            </div>

            <div class="meta-is__user-info">
                <div class="meta-is__user-name">
                    Business Name
                    <br />
                    <span class="meta-is__sponsored">Sponsored</span>
                </div>
            </div>

            <div class="meta-is__options">
                <Icon type="ios-more" />
                <Icon type="md-close" />
            </div>
        </div>

        <div class="post_media single">
            <slot />
        </div>

        <div v-if="metaOverwrites?.fbPrimaryText || metaOverwrites?.fbCallToAction" class="meta-is__description">
            <div v-if="metaOverwrites?.fbPrimaryText" class="meta-is__text">
                {{ metaOverwrites?.fbPrimaryText }}
            </div>

            <div class="meta-is__description__container">
                <button v-if="metaOverwrites?.fbCallToAction" class="meta-is__description-btn">
                    <Icon type="md-link" />
                    {{ metaOverwrites.fbCallToAction }}
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MasterTemplate } from "shared-types";

import { AdSubType } from "@/enums/assemble";
import type { MetaOverwrites } from "@/types/types";
import sanitizeUrl from "@/utils/sanitizeUrl";

export default defineComponent({
    name: "InstagramStory",
    props: {
        metaOverwrites: {
            type: Object as PropType<MetaOverwrites>,
            default: () => ({})
        },

        adSubType: {
            type: String as PropType<(typeof AdSubType)[keyof typeof AdSubType]>,
            default: AdSubType.Video
        },

        masterTemplate: {
            type: Object as PropType<MasterTemplate>
        },

        adSize: {
            type: Number
        }
    },
    data() {
        return {
            safeUrl: ""
        };
    },
    mounted() {
        if (this.metaOverwrites.fbWebsiteUrl) {
            this.safeUrl = this.sanitizeUrl(this.metaOverwrites.fbWebsiteUrl);
        }
    },
    methods: {
        sanitizeUrl
    }
});
</script>

<style lang="scss">
.post_media {
    .banner-preview {
        align-items: center;

        .iframe-container {
            height: 100%;
        }
    }
}
</style>

<style lang="scss" scoped>
.meta-is {
    width: 100%;
    background-color: #bdbdbd;

    display: flex;
    flex-direction: column;
}

.meta-is__info {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
    animation: hide-show 15s linear infinite;
    opacity: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-direction: column;
    font-weight: 600;
    font-size: 18px;

    a {
        display: inline-block;
        color: #fff !important;
        text-decoration: none;
        font-size: 12px;
        margin: 0 0 40px;
    }

    button {
        color: #000;
        background: #fff;
        border-radius: 20px;
        border: 0;
        padding: 10px 15px;
        font-size: 12px;
        font-weight: 600;
    }
}

.meta-is__header {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 3;
    display: flex;
    align-items: center;
    padding: 20px 10px 10px;

    .meta-is__progress {
        height: 100%;
        width: 30%;
        background-color: #ffffff;
        animation: progressAnimation 15s linear infinite;
    }

    .meta-is__progress-bar {
        height: 2px;
        background-color: rgba(205, 205, 205, 0.5);
        border-radius: 3px;
        position: absolute;
        top: 10px;
        left: 7px;
        width: calc(100% - 14px);
        z-index: 3;
    }

    .meta-is__avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        margin-right: 10px;

        i {
            width: 100%;
            height: 100%;
            font-size: 42px;
            color: #fff;
        }
    }

    .meta-is__user-info {
        flex-grow: 1;

        .meta-is__user-name {
            font-weight: bold;
            font-size: 14px;
            color: #fff;
            line-height: 15px;
        }

        .meta-is__sponsored {
            font-weight: normal;
            font-size: 12px;
            color: #fff;
        }

        .meta-is__time {
            color: #fff;
            font-size: 12px;
        }
    }

    .meta-is__options {
        font-size: 18px;
        color: #fff;
        cursor: pointer;
        i {
            &:last-child {
                margin-left: 10px;
            }
        }
    }
}

.post_media {
    flex: 1;

    display: flex;
}

.meta-is__description {
    padding: 16px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 45px;

    .meta-is__text {
        font-size: 14px;
        color: #333;
        font-weight: 600;
    }

    .meta-is__description__container {
        display: flex;
        align-items: center;
        justify-content: center;

        .meta-is__description-btn {
            border: 0;
            font-size: 24px;
            color: #2e4386;
            padding: 0 10px;
            background: #fff;
            border-radius: 6px;
            animation: show-hide 15s linear infinite;
        }
    }
}

@keyframes progressAnimation {
    0% {
        width: 0%;
    }
    100% {
        width: 100%;
    }
}

@keyframes show-hide {
    0% {
        opacity: 1;
    }
    66% {
        opacity: 1;
    }
    70% {
        opacity: 0;
    }
    100% {
        opacity: 0;
    }
}

@keyframes hide-show {
    0% {
        opacity: 0;
    }
    66% {
        opacity: 0;
    }
    70% {
        opacity: 1;
    }
    100% {
        opacity: 1;
    }
}
</style>
